import React, { useEffect, useMemo, useState } from "react";
import { useGet } from "../../api";
import {
  IAffiliateNetworksResponse,
  INetworkCategories,
  IAdvertiserCategories,
  ICategoryEntry,
} from "../../interfaces";
import {
  Button,
  Spinner,
  Badge,
  Table,
  Form,
  Row,
  Col,
  Accordion,
  Pagination,
} from "react-bootstrap";
import { Link } from "react-router";
import { RefreshCw, Filter } from "react-feather";
import { settingsType, useSettings } from "../../hooks";

export const Categories: React.FC = () => {
  const [network, setNetwork] = useSettings(settingsType.AP_CATEGORIES_NETWORK);
  const [searchText, setSearchText] = useSettings(
    settingsType.AP_CATEGORIES_SEARCH
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState("10");

  const networks = useGet<IAffiliateNetworksResponse>(
    "affiliate-products/networks",
    true
  );

  const networkOptions = useMemo(() => {
    const options = [...(networks.data?.items || [])];
    if (!options.includes("Manual")) {
      options.push("Manual");
    }
    return options;
  }, [networks.data]);

  useEffect(() => {
    if (!network && networkOptions.length > 0) {
      setNetwork(networkOptions[0]);
    }
  }, [network, networkOptions, setNetwork]);

  const selectedNetwork = network || networkOptions[0] || "";

  const categories = useGet<INetworkCategories>(
    selectedNetwork
      ? `affiliate-products/categories?network=${selectedNetwork}`
      : null,
    false
  );

  const handleRefresh = () => {
    categories.refetch();
  };

  const filterCategories = (cats: ICategoryEntry[]): ICategoryEntry[] => {
    if (!searchText) return cats;
    const lower = searchText.toLowerCase();
    return cats.filter((c) => c.category.toLowerCase().includes(lower));
  };

  const filteredAdvertisers = useMemo(() => {
    const advertisers = categories.data?.advertisers || [];
    if (!searchText) {
      return advertisers;
    }
    return advertisers.filter(
      (adv) => filterCategories(adv.categories).length > 0
    );
  }, [categories.data, searchText]);

  const pageSizeNumber = Number(pageSize) || 10;
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAdvertisers.length / pageSizeNumber)
  );
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const pageStartIndex = (safeCurrentPage - 1) * pageSizeNumber;
  const pageEndIndex = pageStartIndex + pageSizeNumber;
  const pagedAdvertisers = filteredAdvertisers.slice(
    pageStartIndex,
    pageEndIndex
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedNetwork,
    searchText,
    pageSize,
    categories.data?.advertisers?.length,
  ]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 1) {
      return [] as number[];
    }

    const windowSize = 5;
    const start = Math.max(1, safeCurrentPage - Math.floor(windowSize / 2));
    const end = Math.min(totalPages, start + windowSize - 1);
    const adjustedStart = Math.max(1, end - windowSize + 1);
    return Array.from(
      { length: end - adjustedStart + 1 },
      (_, i) => adjustedStart + i
    );
  }, [safeCurrentPage, totalPages]);

  return (
    <div>
      <h4 className="mb-3">Product Categories</h4>

      <Row className="mb-3 g-2">
        <Col md={3}>
          <Form.Label className="small">Network</Form.Label>
          <Form.Select
            size="sm"
            value={selectedNetwork}
            onChange={(e) => setNetwork(e.target.value)}
          >
            {networkOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label className="small">Filter Categories</Form.Label>
          <Form.Control
            size="sm"
            placeholder="Search category name..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col md={3} className="d-flex align-items-end">
          <Button
            size="sm"
            variant="primary"
            onClick={handleRefresh}
            disabled={categories.loading}
          >
            {categories.loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <RefreshCw size={14} className="me-1" /> Load Categories
              </>
            )}
          </Button>
        </Col>
      </Row>

      {categories.error && (
        <div className="text-danger mb-3">
          Error: {categories.error.message}
        </div>
      )}

      {categories.data && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2 gap-2 flex-wrap">
            <small className="text-muted mb-0">
              <Badge bg="dark">{categories.data.affiliateNetwork}</Badge>{" "}
              {categories.data.advertiserCount} advertisers,{" "}
              {categories.data.totalCategories} total categories
              {filteredAdvertisers.length > 0 &&
                ` - Showing ${pageStartIndex + 1}-${Math.min(pageEndIndex, filteredAdvertisers.length)} of ${filteredAdvertisers.length} advertisers`}
            </small>
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="small mb-0">Per Page</Form.Label>
              <Form.Select
                size="sm"
                value={pageSize}
                onChange={(e) => setPageSize(e.target.value)}
                style={{ width: 90 }}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </Form.Select>
            </div>
          </div>

          <Accordion>
            {pagedAdvertisers.map((adv: IAdvertiserCategories) => {
              const filtered = filterCategories(adv.categories);
              return (
                <Accordion.Item
                  key={`${adv.affiliateNetwork}-${adv.advertiserId}`}
                  eventKey={adv.advertiserId}
                >
                  <Accordion.Header>
                    <div className="d-flex justify-content-between w-100 me-3">
                      <span>
                        <strong>
                          {adv.advertiserName || adv.advertiserId}
                        </strong>
                        <small className="text-muted ms-2">
                          ({adv.advertiserId})
                        </small>
                      </span>
                      <span>
                        <Badge bg="info" className="me-2">
                          {adv.categoryCount} categories
                        </Badge>
                        <Badge bg="secondary">
                          {adv.totalProducts.toLocaleString()} products
                        </Badge>
                      </span>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table
                      striped
                      hover
                      size="sm"
                      style={{ fontSize: "0.85rem" }}
                    >
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Products</th>
                          <th>Last Updated</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((cat: ICategoryEntry) => (
                          <tr key={cat.category}>
                            <td>{cat.category}</td>
                            <td>{cat.productCount.toLocaleString()}</td>
                            <td>
                              <small className="text-muted">
                                {cat.lastUpdatedAt
                                  ? new Date(
                                      cat.lastUpdatedAt
                                    ).toLocaleDateString()
                                  : "—"}
                              </small>
                            </td>
                            <td>
                              <Link
                                to={`/affiliate-products/products?network=${selectedNetwork}&advertiserId=${adv.advertiserId}&category=${encodeURIComponent(cat.category)}`}
                              >
                                <Button size="sm" variant="outline-primary">
                                  <Filter size={12} className="me-1" />
                                  View Products
                                </Button>
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-2">
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev
                  disabled={safeCurrentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                />
                {paginationItems.map((page) => (
                  <Pagination.Item
                    key={page}
                    active={page === safeCurrentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={safeCurrentPage >= totalPages}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
};
